/**
 * MED-1300 — one honest notification per invite send.
 *
 * Finalizing or resending invites previously always showed a green success,
 * whether or not anyone was dropped or delivery fell back to email — which is
 * how a missing calendar event and an uninvited participant went unnoticed for
 * weeks (MDF-193). This helper renders the outcome the API now reports:
 *
 *   clean send                     → green success (AC4 — no crying wolf)
 *   participant(s) not invited     → yellow, naming who and why (AC1/AC2)
 *   delivered by email, not calendar → yellow, saying so (AC3)
 *   both                           → yellow combined state
 *
 * Coach skips never reach this helper — the API excludes them from
 * skipped_participants because a Partner Coach without a Medley email is
 * expected (MED-831), so that case stays on the clean state (AC2).
 *
 * Notifications summarize; the full detail lives in the cohort activity log
 * (AC5/AC6), which every warning points at.
 */
import { notifications } from '@mantine/notifications';
import type { SkippedInviteParticipant } from '../types';

const REASON_TEXT: Record<SkippedInviteParticipant['reason'], string> = {
  missing_email: 'no email address on file',
  user_not_found: 'no user record',
};

/** AC5: name up to two people inline; the activity log holds the full list. */
function summarizeSkipped(skipped: SkippedInviteParticipant[]): string {
  const named = skipped
    .slice(0, 2)
    .map(s => `${s.name ?? 'Unknown participant'} — ${REASON_TEXT[s.reason]}`);
  const more = skipped.length - named.length;
  return `Not invited: ${named.join('; ')}${more > 0 ? ` and ${more} more` : ''}.`;
}

export interface InviteSendOutcome {
  kind: 'session' | 'workshop';
  mode: 'finalize' | 'resend';
  /** Sessions this send processed (1 for a workshop). */
  sessionsProcessed: number;
  /** Best available "people" figure for the clean message, when known. */
  peopleCount?: number;
  skippedParticipants?: SkippedInviteParticipant[];
  /** Sessions delivered by ICS email instead of a native calendar event. */
  emailFallbackSessions?: number;
  /** Residual errors[] length — genuine delivery failures beyond the skips. */
  errorCount?: number;
}

export function showInviteSendOutcome(outcome: InviteSendOutcome): void {
  const {
    kind,
    mode,
    sessionsProcessed,
    peopleCount,
    skippedParticipants = [],
    emailFallbackSessions = 0,
    errorCount = 0,
  } = outcome;

  const verb = mode === 'resend' ? 'resent' : 'sent';
  const unit =
    kind === 'workshop'
      ? `${sessionsProcessed} workshop${sessionsProcessed !== 1 ? 's' : ''}`
      : `${sessionsProcessed} session${sessionsProcessed !== 1 ? 's' : ''}`;
  const hasSkips = skippedParticipants.length > 0;
  // MED-1300 review feedback: when people were dropped, the count must not
  // claim everyone was invited ("2 people. Not invited: george fred" read as
  // a contradiction). The AC copy is "9 of 10 people".
  const delivered =
    peopleCount !== undefined
      ? Math.max(0, peopleCount - skippedParticipants.length)
      : undefined;
  const people =
    peopleCount !== undefined
      ? hasSkips
        ? ` · ${delivered} of ${peopleCount} people`
        : ` · ${peopleCount} ${peopleCount === 1 ? 'person' : 'people'}`
      : '';

  const hasFallback = emailFallbackSessions > 0;
  const allFellBack = hasFallback && emailFallbackSessions >= sessionsProcessed;
  const fallbackClause = allFellBack
    ? `${sessionsProcessed === 1 ? 'This session is' : 'These sessions are'} not on the Medley Team calendar — recipients received an email invitation instead.`
    : `${emailFallbackSessions} of ${sessionsProcessed} sessions went out by email and are not on the Medley Team calendar.`;

  if (hasSkips && hasFallback) {
    notifications.show({
      title: `Invites ${verb} with problems`,
      message: `${unit}${people} · sent by email, not calendar. ${summarizeSkipped(skippedParticipants)} Recorded in the cohort activity log.`,
      color: 'yellow',
      autoClose: false,
    });
    return;
  }

  if (hasSkips) {
    const n = skippedParticipants.length;
    notifications.show({
      title: `Invites ${verb}, ${n} ${n === 1 ? 'person' : 'people'} not invited`,
      message: `${unit}${people}. ${summarizeSkipped(skippedParticipants)} Recorded in the cohort activity log.`,
      color: 'yellow',
      autoClose: false,
    });
    return;
  }

  if (hasFallback) {
    notifications.show({
      title: `Invites ${verb} by email, not calendar`,
      message: `${unit}${people}. ${fallbackClause} Recorded in the cohort activity log.`,
      color: 'yellow',
      autoClose: false,
    });
    return;
  }

  if (errorCount > 0) {
    // Residual delivery failures (e.g. an email failed to queue) with no skips
    // and no fallback — rare, but not a success.
    notifications.show({
      title: `Invites ${verb} with problems`,
      message: `${unit}${people}. ${errorCount} delivery error${errorCount !== 1 ? 's' : ''} — recorded in the cohort activity log.`,
      color: 'yellow',
      autoClose: false,
    });
    return;
  }

  notifications.show({
    title: mode === 'resend' ? 'Invites Resent' : 'Invites Sent',
    message: `${unit}${people} · added to the Medley Team calendar`,
    color: 'green',
  });
}
